import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import EnquiryDialog from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";

export default function Bvoc() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses", { params: { category: "wilp-bvoc", limit: 10 } })
      .then((r) => setCourses(r.data.items)).catch(() => {});
  }, []);

  return (
    <div className="bg-navy-base min-h-screen" data-testid="bvoc-page">
      <SEO title="WILP B.Voc Programs in Jorhat | XTM TECHNOLOGIES"
        description="Work-Integrated Learning Programmes — B.Voc degree pathways in Application Development and Information Technology at XTM FutureSkills Academy, Jorhat. Learn + Work + Build Experience." />
      <section className="relative hero-glow overflow-hidden">
        <div className="absolute inset-0 cyber-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-3">Degree Pathways</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4" data-testid="bvoc-heading">
            Work-Integrated Learning Programme <span className="text-gradient-orange">(WILP)</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl leading-relaxed mb-8">Learn + Work + Build Experience — earn a B.Voc degree while gaining structured industry experience.</p>
          <EnquiryDialog type="counselling" title="B.Voc Admission Counselling" trigger={
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-8 transition-colors" data-testid="bvoc-counselling-btn">Get Admission Guidance</Button>
          } />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-6 mb-16" data-testid="bvoc-programs">
          {courses.map((c) => (
            <div key={c.slug} className="bg-card border border-white/10 rounded-2xl overflow-hidden hover:border-orange-500/40 transition-colors" data-testid={`bvoc-card-${c.slug}`}>
              <img src={c.image} alt={c.title} className="w-full h-48 object-cover" loading="lazy" />
              <div className="p-7">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-5 h-5 text-orange-400" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-orange-300 border border-orange-500/40 rounded-full px-2.5 py-1">WILP Degree</span>
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-4">{c.title}</h3>
                <ul className="space-y-2 mb-6">
                  {[["Duration", c.duration], ["Eligibility", c.eligibility], ["Mode", c.mode], ["Certification", c.certification], ...(c.university ? [["University / Collaboration", c.university]] : [])].map(([k, v]) => (
                    <li key={k} className="text-sm text-slate-400 flex gap-2"><CheckCircle2 className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" /><span><strong className="text-slate-200">{k}:</strong> {v}</span></li>
                  ))}
                </ul>
                <div className="flex gap-3">
                  <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full px-6 transition-colors" data-testid={`bvoc-apply-${c.slug}`}>
                    <Link to={`/admissions?course=${c.slug}`}>Apply Now</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full px-6 transition-colors" data-testid={`bvoc-details-${c.slug}`}>
                    <Link to={`/course/${c.slug}`}>Details</Link>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-card border border-white/10 rounded-2xl p-8" data-testid="bvoc-how-it-works">
          <h2 className="font-display text-2xl font-bold text-white mb-6">How WILP Works</h2>
          <div className="grid sm:grid-cols-4 gap-6">
            {["Semester-based academic learning", "Structured work-integrated training", "Industry projects every semester", "Degree + experience on graduation"].map((s, i) => (
              <div key={s} className="text-left">
                <div className="w-9 h-9 rounded-full bg-orange-600 text-white font-display font-bold flex items-center justify-center mb-3">{i + 1}</div>
                <p className="text-sm text-slate-300">{s}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-6">XTM TECHNOLOGIES is an Industry Collaborator with Scope Global Skill University (SGSU), Bhopal for implementing the WILP vocational programs for B.Voc. in Application Development and B.Voc. in Information Technology, and is the Knowledge Skill Provider of AISECT India. Credits, fees, semester structure and application deadlines are confirmed during counselling.</p>
        </div>
      </section>
    </div>
  );
}
